import {
  Globe, Network, Smartphone, Phone, Monitor, Package, Cpu,
  Wifi, Shield, Router, PhoneCall, Cloud, Mail, HardDrive, Handshake
} from "lucide-react";

export interface ServiceItem {
  name: string;
  description: string;
  price?: string;
  priceNote?: string;
}

export interface ServiceCategory {
  title: string;
  slug: string;
  description: string;
  longDescription: string;
  icon: typeof Globe;
  color: string;
  image: string;
  imageAlt: string;
  items: ServiceItem[];
}

export const services: ServiceCategory[] = [
  {
    title: "Internet",
    slug: "internet",
    description: "Supersnel zakelijk internet, altijd online",
    longDescription: "Een betrouwbare internetverbinding is de basis van alles. Wij regelen de snelste verbinding — en als het hapert, heb je altijd een backup.",
    icon: Globe,
    color: "text-primary",
    image: "https://images.unsplash.com/photo-1544197150-b99a580bb7a8?w=800&q=80&auto=format",
    imageAlt: "Glasvezelkabels met blauw licht in een serverruimte",
    items: [
      { name: "Rinqel VDSL", description: "30 tot 200 Mbit download, tot 60 Mbit upload. Betrouwbaar internet via de telefoonlijn.", price: "Vanaf €55", priceNote: "per maand" },
      { name: "Rinqel Glasvezel", description: "50 tot 4000 Mbit, upload = download. De snelste verbinding voor veeleisende bedrijven.", price: "Vanaf €65", priceNote: "per maand" },
      { name: "Rinqel 5G Onbeperkt", description: "Onbeperkt snel mobiel internet. Ideaal als vast alternatief.", price: "Binnenkort", priceNote: "" },
      { name: "Rinqel 5G Backup", description: "Automatische failover via 5G als je vaste lijn uitvalt. Altijd online.", price: "Binnenkort", priceNote: "" },
      { name: "Rinqel met Starlink", description: "Snelheden tot 305 Mbit download, tot 40 Mbit upload. Internet overal, ook waar geen kabel komt.", price: "Vanaf €65", priceNote: "per maand" },
    ],
  },
  {
    title: "Netwerken",
    slug: "netwerken",
    description: "Professionele netwerken die gewoon werken",
    longDescription: "Geen gedoe met netwerken die haperen of WiFi die niet werkt in de vergaderkamer. Wij bouwen én beheren je complete bedrijfsnetwerk.",
    icon: Network,
    color: "text-primary",
    image: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800&q=80&auto=format",
    imageAlt: "Netwerkswitches met aangesloten kabels in een serverrack",
    items: [
      { name: "Internet", description: "Snelle, stabiele verbinding met de buitenwereld", price: "", priceNote: "" },
      { name: "Router / Firewall", description: "Veilig internet met professionele beveiliging", price: "Inbegrepen", priceNote: "in internet abonnement" },
      { name: "Switch", description: "Verbindt al je apparaten via kabels met het netwerk", price: "€5", priceNote: "per switch/per maand" },
      { name: "Access Points", description: "Overal sterk WiFi-signaal voor iedereen", price: "€2,50", priceNote: "per accesspoint/per maand" },
    ],
  },
  {
    title: "Zakelijk Mobiel",
    slug: "mobiel",
    description: "Flexibel bellen en internetten onderweg",
    longDescription: "Zakelijke mobiele abonnementen die écht flexibel zijn. Dagelijks aanpasbaar, maandelijks opzegbaar. Bij KPN, Vodafone of Odido.",
    icon: Smartphone,
    color: "text-primary",
    image: "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=800&q=80&auto=format",
    imageAlt: "Zakenman die smartphone gebruikt onderweg",
    items: [
      { name: "KPN", description: "Het grootste en meest betrouwbare netwerk van Nederland", price: "Vanaf €12", priceNote: "per maand" },
      { name: "Vodafone", description: "Razendsnelle 5G-dekking en uitstekende internationale opties", price: "Vanaf €11", priceNote: "per maand" },
      { name: "Odido", description: "Voordelig en flexibel met uitstekende 5G-dekking", price: "Vanaf €10", priceNote: "per maand" },
    ],
  },
  {
    title: "Zakelijke Telefonie",
    slug: "telefonie",
    description: "Professioneel bellen zonder gedoe",
    longDescription: "Bel je klanten professioneel, vanuit kantoor, thuis of onderweg. Wij regelen alles: van telefoonnummers tot integratie met je CRM.",
    icon: Phone,
    color: "text-primary",
    image: "https://images.unsplash.com/photo-1521791136064-7986c2920216?w=800&q=80&auto=format",
    imageAlt: "Modern kantoor met telefoontoestel op bureau",
    items: [
      { name: "VoIP Centrale", description: "Complete telefooncentrale in de cloud, geen hardware nodig", price: "Vanaf €15", priceNote: "per maand" },
      { name: "VoIP Aansluiting", description: "Bellen via internet per gebruiker of toestel", price: "Vanaf €6", priceNote: "per maand" },
      { name: "Bellen met Teams", description: "Bel direct vanuit Microsoft Teams", price: "Vanaf €5", priceNote: "per maand" },
      { name: "Softphone / App bellen", description: "Bel vanaf je laptop of telefoon via de Rinqel-app", price: "Inbegrepen", priceNote: "" },
      { name: "VAMO – Vast-Mobiel Integratie", description: "Je vaste nummer ook op je mobiel, altijd bereikbaar", price: "Vanaf €2,50", priceNote: "per maand" },
      { name: "CRM & ERP koppelingen", description: "Koppel je telefonie aan je bedrijfssoftware", price: "Vanaf €5", priceNote: "per maand" },
    ],
  },
  {
    title: "Digitale Werkplek",
    slug: "werkplek",
    description: "Overal veilig en productief werken",
    longDescription: "Je werkplek is meer dan een laptop. Wij zorgen dat je overal kunt werken — veilig, snel en zonder gedoe. Met Microsoft 365 als basis.",
    icon: Monitor,
    color: "text-primary",
    image: "https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=800&q=80&auto=format",
    imageAlt: "Laptop op een modern bureau in een lichte werkruimte",
    items: [
      { name: "Managed Cloud Only Werkplek", description: "Werken vanuit de cloud met Microsoft 365 Business Basic als basis. E-mail, OneDrive, Teams en SharePoint — inclusief back-up en support.", price: "€25", priceNote: "/per maand" },
      { name: "Managed Werkplek", description: "De complete werkplek met Microsoft 365 Business Premium. Alles van Cloud Only plus geavanceerde beveiliging en apparaatbeheer — inclusief back-up en support.", price: "€50", priceNote: "/per maand" },
      { name: "Managed Kiosk Werkplek", description: "Voor gedeelde apparaten op de winkelvloer, in het magazijn of de zorg. Vergrendeld op alleen de apps die nodig zijn — maximaal veilig en simpel.", price: "€25", priceNote: "/per maand" },
      { name: "Managed Mobile Device Only", description: "Voor medewerkers die alleen een tablet of telefoon gebruiken. Op basis van Microsoft 365 F3 als basis — inclusief back-up en support.", price: "€17,50", priceNote: "/per maand" },
      { name: "Losse Mailbox", description: "Een zakelijke mailbox op basis van Exchange Online. Te gebruiken via de webbrowser of mail-app — inclusief back-up en support.", price: "€7", priceNote: "/per maand" },
      { name: "Gedeelde Mailbox", description: "Voor adressen als info@, verkoop@ of administratie@. Toegankelijk voor meerdere collega's — inclusief back-up en support.", price: "€2", priceNote: "/per maand" },
    ],
  },
  {
    title: "Software",
    slug: "software",
    description: "Alle software licenties geregeld",
    longDescription: "De juiste software voor je team, altijd up-to-date en goed beheerd. Van Microsoft 365 tot Adobe en Autodesk.",
    icon: Package,
    color: "text-primary",
    image: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&q=80&auto=format",
    imageAlt: "Team werkend achter computerschermen in een kantoor",
    items: [
      { name: "Microsoft 365", description: "Office, Teams, OneDrive en meer", price: "Vanaf €10,50", priceNote: "per gebruiker/per maand" },
      { name: "Adobe Creative Cloud", description: "Photoshop, Illustrator en de hele Adobe-suite", price: "Op aanvraag", priceNote: "" },
      { name: "Autodesk", description: "AutoCAD, Revit en andere Autodesk-producten", price: "Op aanvraag", priceNote: "" },
    ],
  },
  {
    title: "Hardware",
    slug: "hardware",
    description: "Alles wat je nodig hebt, geleverd en geïnstalleerd",
    longDescription: "Van laptops tot printers, van monitoren tot netwerkapparatuur. Wij leveren alles wat je nodig hebt — en zorgen dat het werkt.",
    icon: Cpu,
    color: "text-primary",
    image: "https://images.unsplash.com/photo-1496181133206-80ce9b88a853?w=800&q=80&auto=format",
    imageAlt: "Laptops en hardware apparatuur op een bureau",
    items: [
      { name: "Laptops & Desktops", description: "De beste werkplekken voor je team", price: "Op aanvraag", priceNote: "" },
      { name: "Telefoons & Tablets", description: "Zakelijke mobiele apparaten", price: "Op aanvraag", priceNote: "" },
      { name: "Monitoren", description: "Scherpe beelden voor productief werken", price: "Op aanvraag", priceNote: "" },
      { name: "Printers & Scanners", description: "Printen en scannen zonder gedoe", price: "Op aanvraag", priceNote: "" },
      { name: "Netwerkapparatuur", description: "Routers, switches en WiFi accesspoints", price: "Op aanvraag", priceNote: "" },
      { name: "Camera's & Opname apparatuur", description: "Beveiligingscamera's en opnamesystemen voor kantoor en bedrijfspand", price: "Op aanvraag", priceNote: "" },
    ],
  },
  {
    title: "Alles-in-één",
    slug: "alles-in-een",
    description: "1 aanspreekpunt, geen kastje naar de muur",
    longDescription: "Wij regelen alles voor je: internet, telefonie, netwerken, werkplekken en meer. Eén vast aanspreekpunt, geen gedoe.",
    icon: Handshake,
    color: "text-primary",
    image: "",
    imageAlt: "",
    items: [],
  },
];
